import { inject } from '@angular/core';
import {
  companyFormSchema,
  createCompanyFormValue,
  getCompanyEditPayload,
} from '@indutrust/company-util';
import {
  withEntityFormFromSource,
  withEntityId,
  withSignalForm,
} from '@indutrust/shared-util';
import { signalStore, withMethods } from '@ngrx/signals';
import { injectDispatch } from '@ngrx/signals/events';
import { companySingleEvents } from '../../+state/company-detail.events';
import { CompanyDetailStore } from '../../+state/company-detail.store';

/**
 * Scoped store owning the company edit signal form lifecycle.
 *
 * Composes `withEntityId` + `withSignalForm` + `withEntityFormFromSource` from
 * `@indutrust/shared-util` (Pattern D). Loads the company from
 * {@link CompanyDetailStore} using the route `:id` segment (set via
 * `setEntityId()` from the host component). Syncs the form when the entity
 * arrives and exposes `undo()` / `resetFromEntity()`.
 *
 * `save({ isAdmin, canMutate })` accepts caller flags rather than injecting
 * `AuthSessionStore` directly — this avoids a circular dependency between
 * `company-data` and `auth-data`. `canMutate` gates R5 Viewer read-only saves.
 *
 * **Security note:** the `entityEditableGuard` redesign (cleanup-pending
 * Groups N + T) is the planned frontend defense-in-depth for edit ownership.
 * Until that lands, unauthorized PATCHes are rejected server-side per R12.
 *
 * @example
 * const formStore = inject(CompanyEditFormStore);
 * formStore.setEntityId(42);
 * formStore.save(authStore.isAdmin());
 */
export const CompanyEditFormStore = signalStore(
  withEntityId(),
  withSignalForm({
    createEmptyValue: createCompanyFormValue,
    schema: companyFormSchema,
  }),
  withEntityFormFromSource({
    entityStore: () => inject(CompanyDetailStore),
    mapEntityToFormValue: (entity) => createCompanyFormValue(entity),
  }),
  withMethods((store) => {
    const dispatch = injectDispatch(companySingleEvents);

    return {
      save(options: { canMutate: boolean; isAdmin: boolean }) {
        if (!options.canMutate) {
          return;
        }
        const entityId = store.entityId();
        if (entityId === null || entityId <= 0) {
          return;
        }
        dispatch.editRequested({
          id: entityId,
          payload: getCompanyEditPayload(store.formModel(), options.isAdmin),
        });
      },
    };
  })
);
